import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLLists = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Content & Typography</div>
                <h1>Lists: Ordered, Unordered, Description</h1>
                <p className="hero-lead">
                    Master the three types of HTML lists. Learn when to use ordered, unordered, and description lists
                    for organizing content, creating navigation menus, and displaying structured information.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Lists Matter</h2>
                <p>
                    Lists are fundamental to web content organization. They improve readability, enhance SEO,
                    and provide semantic meaning to grouped information.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Better Readability</h3>
                        <p>Lists break up text and make scanning content easier</p>
                    </div>
                    <div className="benefit-card">
                        <h3> SEO Benefits</h3>
                        <p>Search engines recognize lists as structured, valuable content</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <p>Screen readers announce list items count and navigate efficiently</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Easy Styling</h3>
                        <p>CSS provides powerful list styling options</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Unordered Lists (&lt;ul&gt;)</h2>
                <p>
                    Unordered lists display items with bullet points. Use them when the order doesn't matter.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Unordered List</span>
                    </div>
                    <CodeBlock language="html" code={`<ul>
    <li>HTML</li>
    <li>CSS</li>
    <li>JavaScript</li>
</ul>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <ul>
                        <li>HTML</li>
                        <li>CSS</li>
                        <li>JavaScript</li>
                    </ul>
                </div>

                <h3>When to Use Unordered Lists:</h3>
                <ul>
                    <li>Shopping lists</li>
                    <li>Feature lists</li>
                    <li>Navigation menus</li>
                    <li>Tag clouds</li>
                    <li>Any collection where order doesn't matter</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Ordered Lists (&lt;ol&gt;)</h2>
                <p>
                    Ordered lists display numbered items. Use them when sequence or ranking matters.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Ordered List</span>
                    </div>
                    <CodeBlock language="html" code={`<ol>
    <li>Open your text editor</li>
    <li>Create a new HTML file</li>
    <li>Write your HTML code</li>
    <li>Save and open in browser</li>
</ol>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <ol>
                        <li>Open your text editor</li>
                        <li>Create a new HTML file</li>
                        <li>Write your HTML code</li>
                        <li>Save and open in browser</li>
                    </ol>
                </div>

                <h3>When to Use Ordered Lists:</h3>
                <ul>
                    <li>Step-by-step instructions</li>
                    <li>Rankings (top 10 lists)</li>
                    <li>Recipes</li>
                    <li>Table of contents</li>
                    <li>Any sequence that must be followed in order</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Ordered List Attributes</h2>
                <p>
                    Ordered lists support attributes to customize numbering:
                </p>

                <h3>1. The <code>type</code> Attribute</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Different Numbering Styles</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Numbers (default) --&gt;
<ol type="1">
    <li>First item</li>
    <li>Second item</li>
</ol>

<!-- Uppercase letters --&gt;
<ol type="A">
    <li>First item</li>
    <li>Second item</li>
</ol>

<!-- Lowercase letters --&gt;
<ol type="a">
    <li>First item</li>
    <li>Second item</li>
</ol>

<!-- Uppercase Roman numerals --&gt;
<ol type="I">
    <li>First item</li>
    <li>Second item</li>
</ol>

<!-- Lowercase Roman numerals --&gt;
<ol type="i">
    <li>First item</li>
    <li>Second item</li>
</ol>`} />
                </div>

                <div className="type-comparison">
                    <table>
                        <thead>
                            <tr>
                                <th>Type Value</th>
                                <th>Style</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>type="1"</code></td>
                                <td>Numbers (default)</td>
                                <td>1, 2, 3, 4...</td>
                            </tr>
                            <tr>
                                <td><code>type="A"</code></td>
                                <td>Uppercase letters</td>
                                <td>A, B, C, D...</td>
                            </tr>
                            <tr>
                                <td><code>type="a"</code></td>
                                <td>Lowercase letters</td>
                                <td>a, b, c, d...</td>
                            </tr>
                            <tr>
                                <td><code>type="I"</code></td>
                                <td>Uppercase Roman</td>
                                <td>I, II, III, IV...</td>
                            </tr>
                            <tr>
                                <td><code>type="i"</code></td>
                                <td>Lowercase Roman</td>
                                <td>i, ii, iii, iv...</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>2. The <code>start</code> Attribute</h3>
                <p>Start numbering from a specific value:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Start at 5 --&gt;
<ol start="5">
    <li>Fifth item</li>
    <li>Sixth item</li>
    <li>Seventh item</li>
</ol>

<!-- Renders as: 5, 6, 7 --&gt;`} />
                </div>

                <h3>3. The <code>reversed</code> Attribute</h3>
                <p>Count down instead of up (boolean attribute):</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Countdown list --&gt;
<ol reversed>
    <li>Third place</li>
    <li>Second place</li>
    <li>First place (winner!)</li>
</ol>

<!-- Renders as: 3, 2, 1 --&gt;`} />
                </div>

                <h3>4. The <code>value</code> Attribute (on &lt;li&gt;)</h3>
                <p>Set a specific number for an individual list item:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<ol>
    <li>Item 1</li>
    <li>Item 2</li>
    <li value="10">Item 10 (jumped from 2)</li>
    <li>Item 11 (continues from 10)</li>
</ol>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Description Lists (&lt;dl&gt;)</h2>
                <p>
                    Description lists display term-description pairs. Perfect for glossaries, metadata, and key-value pairs.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Structure:</strong> Description lists use three elements:
                        <ul>
                            <li><code>&lt;dl&gt;</code> - Description List (container)</li>
                            <li><code>&lt;dt&gt;</code> - Description Term (the term being defined)</li>
                            <li><code>&lt;dd&gt;</code> - Description Definition (the description/definition)</li>
                        </ul>
                    </div></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Description List</span>
                    </div>
                    <CodeBlock language="html" code={`<dl>
    <dt>HTML</dt>
    <dd>HyperText Markup Language - the structure of web pages</dd>

    <dt>CSS</dt>
    <dd>Cascading Style Sheets - the styling of web pages</dd>

    <dt>JavaScript</dt>
    <dd>Programming language for interactive web pages</dd>
</dl>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <dl>
                        <dt><strong>HTML</strong></dt>
                        <dd>HyperText Markup Language - the structure of web pages</dd>

                        <dt><strong>CSS</strong></dt>
                        <dd>Cascading Style Sheets - the styling of web pages</dd>

                        <dt><strong>JavaScript</strong></dt>
                        <dd>Programming language for interactive web pages</dd>
                    </dl>
                </div>

                <h3>When to Use Description Lists:</h3>
                <ul>
                    <li>Glossaries and dictionaries</li>
                    <li>Metadata (author, date, categories)</li>
                    <li>FAQ sections</li>
                    <li>Product specifications</li>
                    <li>Contact information</li>
                </ul>

                <h3>Advanced: Multiple Terms or Definitions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- One term with multiple definitions --&gt;
<dl>
    <dt>Color</dt>
    <dd>Visual perception of light wavelengths</dd>
    <dd>A property that adds aesthetic appeal</dd>
</dl>

<!-- Multiple terms with one definition --&gt;
<dl>
    <dt>HTML</dt>
    <dt>HyperText Markup Language</dt>
    <dd>The standard markup language for web pages</dd>
</dl>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Nested Lists</h2>
                <p>
                    Lists can be nested inside other lists to create hierarchies:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Nested Unordered Lists</span>
                    </div>
                    <CodeBlock language="html" code={`<ul>
    <li>Web Development
        <ul>
            <li>Front-End
                <ul>
                    <li>HTML</li>
                    <li>CSS</li>
                    <li>JavaScript</li>
                </ul>
            </li>
            <li>Back-End
                <ul>
                    <li>Node.js</li>
                    <li>Python</li>
                    <li>PHP</li>
                </ul>
            </li>
        </ul>
    </li>
    <li>Mobile Development</li>
</ul>`} />
                </div>

                <div className="render-preview">
                    <h3>Renders as:</h3>
                    <ul>
                        <li>Web Development
                            <ul>
                                <li>Front-End
                                    <ul>
                                        <li>HTML</li>
                                        <li>CSS</li>
                                        <li>JavaScript</li>
                                    </ul>
                                </li>
                                <li>Back-End
                                    <ul>
                                        <li>Node.js</li>
                                        <li>Python</li>
                                        <li>PHP</li>
                                    </ul>
                                </li>
                            </ul>
                        </li>
                        <li>Mobile Development</li>
                    </ul>
                </div>

                <Warning><strong> Important:</strong> Nest lists <em>inside</em> <code>&lt;li&gt;</code> elements, not directly inside <code>&lt;ul&gt;</code> or <code>&lt;ol&gt;</code>.</Warning>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct Nesting</span>
                    </div>
                    <CodeBlock language="html" code={`<ul>
    <li>Item 1
        <ul>
            <li>Nested item</li>
        </ul>
    </li>
</ul>`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Incorrect Nesting</span>
                    </div>
                    <CodeBlock language="html" code={`<ul>
    <li>Item 1</li>
    <ul>  <!-- Wrong! Should be inside <li> --&gt;
        <li>Nested item</li>
    </ul>
</ul>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Styling Lists with CSS</h2>
                <p>
                    CSS provides powerful options for customizing list appearance:
                </p>

                <h3>List Style Type</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`/* Remove bullets/numbers */
ul {
    list-style-type: none;
}

/* Change bullet style */
ul {
    list-style-type: square;  /* or circle, disc */
}

/* Change numbering style */
ol {
    list-style-type: lower-roman;  /* i, ii, iii */
}

/* Custom marker with ::marker pseudo-element */
li::marker {
    content: " ";
    color: green;
    font-weight: bold;
}`} />
                </div>

                <h3>List Style Position</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`/* Bullets outside content (default) */
ul {
    list-style-position: outside;
}

/* Bullets inside content */
ul {
    list-style-position: inside;
}`} />
                </div>

                <h3>Custom Bullet Images</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`ul {
    list-style-image: url('bullet-icon.png');
}

/* Better approach with ::before pseudo-element */
ul {
    list-style: none;
    padding-left: 0;
}

ul li::before {
    content: " ";
    color: blue;
    font-weight: bold;
    margin-right: 8px;
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical List Examples</h2>

                <h3>Navigation Menu</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<nav>
    <ul>
        <li><a href="/">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
nav ul {
    list-style: none;
    padding: 0;
    display: flex;
    gap: 20px;
}

nav li a {
    text-decoration: none;
    padding: 10px 20px;
    background: #007bff;
    color: white;
    border-radius: 5px;
}
</style>`} />
                </div>

                <h3>Product Features</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<h3>Key Features:</h3>
<ul>
    <li>Fast performance</li>
    <li>Secure encryption</li>
    <li>24/7 support</li>
    <li>Money-back guarantee</li>
</ul>`} />
                </div>

                <h3>Recipe Instructions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<h3>Chocolate Chip Cookies Recipe</h3>
<h4>Ingredients:</h4>
<ul>
    <li>2 cups flour</li>
    <li>1 cup butter</li>
    <li>1 cup sugar</li>
    <li>2 eggs</li>
    <li>1 cup chocolate chips</li>
</ul>

<h4>Instructions:</h4>
<ol>
    <li>Preheat oven to 350F (175C)</li>
    <li>Mix butter and sugar until creamy</li>
    <li>Add eggs and mix well</li>
    <li>Gradually add flour</li>
    <li>Fold in chocolate chips</li>
    <li>Bake for 10-12 minutes</li>
</ol>`} />
                </div>

                <h3>FAQ Section</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<h2>Frequently Asked Questions</h2>
<dl>
    <dt>What is HTML?</dt>
    <dd>HTML (HyperText Markup Language) is the standard language for creating web pages.</dd>

    <dt>Is HTML a programming language?</dt>
    <dd>No, HTML is a markup language, not a programming language. It structures content but doesn't have logic.</dd>

    <dt>Do I need to know CSS to use HTML?</dt>
    <dd>While you can use HTML alone, CSS is highly recommended for styling and layout.</dd>
</dl>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Best Practices</h2>

                <div className="accessibility-tips">
                    <h3>Screen Reader Considerations:</h3>
                    <ul>
                        <li>
                            <strong>Lists are announced:</strong> Screen readers announce "List with 5 items"
                            and "List item 1 of 5" for each item
                        </li>
                        <li>
                            <strong>Navigation shortcuts:</strong> Users can jump between lists using keyboard shortcuts
                        </li>
                        <li>
                            <strong>Proper nesting:</strong> Nested lists are announced with correct hierarchy
                        </li>
                    </ul>
                </div>

                <div className="tip-box">
                    <strong> Accessibility Tips:</strong>
                    <ul>
                        <li>Don't use lists purely for indentation or styling</li>
                        <li>Use actual list elements instead of <code>&lt;br&gt;</code> tags</li>
                        <li>Keep list items concise</li>
                        <li>Use appropriate list type (ordered vs unordered)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use unordered lists when order doesn't matter</li>
                            <li>Use ordered lists for sequential steps</li>
                            <li>Use description lists for term-definition pairs</li>
                            <li>Nest lists inside <code>&lt;li&gt;</code> elements</li>
                            <li>Keep list items concise and scannable</li>
                            <li>Use semantic meaning, not just styling</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use lists purely for indentation</li>
                            <li>Nest lists directly in <code>&lt;ul&gt;</code>/<code>&lt;ol&gt;</code></li>
                            <li>Use <code>&lt;br&gt;</code> tags instead of list items</li>
                            <li>Put non-<code>&lt;li&gt;</code> elements directly in lists</li>
                            <li>Use ordered lists when sequence doesn't matter</li>
                            <li>Forget to close list elements properly</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLLists;



